import { enableProdMode } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import { AppModule } from "./app/app.module";
import { environment } from "./environments/environment";
import { SafeArea } from "capacitor-plugin-safe-area";

if (environment.production) {
  enableProdMode();
}

/**
 * Size the app to the area the WebView actually shows, and stop applying
 * safe-area padding for parts of the screen the WebView doesn't cover.
 *
 * Some builds lay the WebView out below the status bar (e.g. iOS applying
 * its automatic content inset) while still reporting the full status-bar
 * safe-area inset - measured on an iPhone: screen 956px, innerHeight 894px,
 * inset top 62px. Sizing to 100svh (full screen) and padding by the inset
 * then pushed the header down by an extra status bar and the tab bar off
 * the bottom. Here the part of the screen outside the viewport is treated
 * as already "used" by the inset it overlaps (status bar first, then home
 * indicator), and the shell height follows window.innerHeight.
 */
function readInsetPx(property: string, envName: string): number {
  const root = document.documentElement;
  const plugin = parseFloat(root.style.getPropertyValue(property)) || 0;
  const probe = document.createElement("div");
  probe.style.cssText = `position:fixed;visibility:hidden;padding-top:env(${envName}, 0px);`;
  document.body.appendChild(probe);
  const env = parseFloat(getComputedStyle(probe).paddingTop) || 0;
  probe.remove();
  return Math.max(plugin, env);
}

function syncViewportMetrics(): void {
  if (!document.body) return;
  // A focused input means the keyboard may be resizing the viewport - keep
  // the layout stable and re-measure on blur instead
  const active = document.activeElement as HTMLElement | null;
  if (
    active &&
    (active.tagName === "INPUT" ||
      active.tagName === "TEXTAREA" ||
      active.isContentEditable)
  ) {
    return;
  }

  const root = document.documentElement;
  const innerHeight = window.innerHeight;
  const portrait = window.innerHeight >= window.innerWidth;
  const screenHeight = portrait
    ? Math.max(screen.width, screen.height)
    : Math.min(screen.width, screen.height);
  const uncovered = Math.max(0, Math.round(screenHeight - innerHeight));

  const insetTop = readInsetPx(
    "--computed-safe-area-top",
    "safe-area-inset-top"
  );
  const insetBottom = readInsetPx(
    "--computed-safe-area-bottom",
    "safe-area-inset-bottom"
  );
  const gapTop = Math.min(uncovered, insetTop);
  const gapBottom = Math.min(Math.max(0, uncovered - insetTop), insetBottom);

  root.style.setProperty("--app-height", `${innerHeight}px`);
  root.style.setProperty("--viewport-gap-top", `${gapTop}px`);
  root.style.setProperty("--viewport-gap-bottom", `${gapBottom}px`);
}

window.addEventListener("resize", syncViewportMetrics);
window.addEventListener("orientationchange", () =>
  setTimeout(syncViewportMetrics, 250)
);
window.addEventListener("pageshow", syncViewportMetrics);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") syncViewportMetrics();
});
document.addEventListener("focusout", () =>
  setTimeout(syncViewportMetrics, 150)
);
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", syncViewportMetrics);
} else {
  syncViewportMetrics();
}

// Initialize SafeArea CSS variables without blocking bootstrap
(async function initSafeArea() {
  try {
    const { insets } = await SafeArea.getSafeAreaInsets();
    const root = document.documentElement;
    root.style.setProperty("--computed-safe-area-top", `${insets.top}px`);
    root.style.setProperty("--computed-safe-area-bottom", `${insets.bottom}px`);
    root.style.setProperty("--computed-safe-area-left", `${insets.left}px`);
    root.style.setProperty("--computed-safe-area-right", `${insets.right}px`);
    syncViewportMetrics();

    // Only add listener if getSafeAreaInsets succeeded
    try {
      // Update on changes (e.g., rotation, status bar visibility)
      await SafeArea.addListener("safeAreaChanged", ({ insets }) => {
        const root = document.documentElement;
        root.style.setProperty("--computed-safe-area-top", `${insets.top}px`);
        root.style.setProperty(
          "--computed-safe-area-bottom",
          `${insets.bottom}px`
        );
        root.style.setProperty("--computed-safe-area-left", `${insets.left}px`);
        root.style.setProperty(
          "--computed-safe-area-right",
          `${insets.right}px`
        );
        syncViewportMetrics();
      });
    } catch (e) {
      // Listener not available on some platforms; ignore
    }
  } catch (e) {
    // Ignore if plugin not available (web/desktop); CSS env() fallback will apply
    // console.warn('SafeArea init failed', e);
  }
})().catch(() => {
  // Catch any unhandled promise rejections from SafeArea
});

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
